import {track as trackMossAnalytics} from '@getmoss/moss-analytics'
import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import cn from 'classnames'
import {motion} from 'framer-motion'
import Link from 'next/link'
import {useLinkResolver} from 'prismicio'
import type {BlogPostDocument} from 'prismicio-types'
import type {FC} from 'react'
import {useEffect, useRef, useState} from 'react'
// eslint-disable-next-line boundaries/dependencies -- grandfathered cross-slice import, predates the slice-boundaries rule
import {formatDate} from 'slices/BlogPostsContainer/utils'
import {EVENTS, track} from 'src/common/helpers'
import {useGetBlogPostCategory} from 'src/common/hooks/useGetBlogPostCategory'
import {useGetDomainCountry} from 'src/common/hooks/useGetDomainCountry'
import {Icon} from 'src/common/Icon'
import type {DomainCountry} from 'src/common/types'
import 'swiper/css'
import {Autoplay} from 'swiper/modules'
import type {SwiperClass} from 'swiper/react'
import {Swiper, SwiperSlide} from 'swiper/react'
import {useProgressBarRef} from '../useProgressBarRef'

export type ResolvedPost = {
  postDocument: BlogPostDocument<string>
}

type FeaturedPostsSliderProps = {
  posts: ResolvedPost[]
  duration: number | null
  buttonLabel?: string
  pageUid: string
  componentIndex: number
}

export const FeaturedPostsSlider: FC<FeaturedPostsSliderProps> = ({
  posts,
  duration,
  buttonLabel,
  pageUid,
  componentIndex,
}) => {
  const [swiperInstance, setSwiperInstance] = useState<SwiperClass | null>(null)
  const [activeIndex, setActiveIndex] = useState<number>(0)
  const [maxMobileHeight, setMaxMobileHeight] = useState<number>(0)
  const {progressBarRef, updateProgress} = useProgressBarRef()
  const domainCountry = useGetDomainCountry()
  const slideRefs = useRef<(HTMLDivElement | null)[]>([])

  const elementsArray = Array.from({length: posts.length}, (_, i) => i)

  // Measure slide heights on mobile to equalize them
  useEffect(() => {
    const measureHeights = () => {
      if (window.innerWidth >= 1024) {
        setMaxMobileHeight(0)

        return
      }

      const heights = slideRefs.current
        .filter((ref): ref is HTMLDivElement => ref !== null)
        .map((ref) => ref.offsetHeight)

      if (heights.length > 0) {
        setMaxMobileHeight(Math.max(...heights))
      }
    }

    const timeoutId = setTimeout(measureHeights, 100)

    window.addEventListener('resize', measureHeights)

    return () => {
      clearTimeout(timeoutId)
      window.removeEventListener('resize', measureHeights)
    }
  }, [posts])

  return (
    <div className="col-span-full flex w-full flex-col items-start">
      <div className="relative w-full">
        <Swiper
          autoplay={{
            delay: (duration ?? 8) * 1000,
          }}
          onAutoplayTimeLeft={(_, __, progress) => {
            updateProgress(progress)
          }}
          onActiveIndexChange={(swiper) => setActiveIndex(swiper.activeIndex)}
          slidesPerView={1}
          spaceBetween={24}
          modules={[Autoplay]}
          breakpoints={{
            1024: {
              slidesPerView: 'auto',
              centeredSlides: true,
              spaceBetween: 24,
            },
          }}
          className="!z-0 col-span-full w-full lg:!overflow-visible"
          onSwiper={(swiper) => setSwiperInstance(swiper)}
        >
          {posts.map((post, index) => (
            <SwiperSlide key={index} className="!w-full lg:!w-full lg:max-w-[1200px]">
              <FeaturedPost
                post={post}
                index={index}
                activeIndex={activeIndex}
                slideRefs={slideRefs}
                maxMobileHeight={maxMobileHeight}
                buttonLabel={buttonLabel}
                pageUid={pageUid}
                componentIndex={componentIndex}
                domainCountry={domainCountry}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      <div className="col-span-full box-border flex w-full items-center justify-center gap-12 text-clip rounded-4 bg-moss-grey-100 pb-0 pt-8 md:h-20 md:p-8">
        <button
          onClick={() => swiperInstance?.slidePrev()}
          className="flex items-center justify-center"
          aria-label="Previous slide"
        >
          <Icon
            name="arrow"
            className={cn('h-3 w-[13.595px] rotate-180', {
              'text-moss-grey-400': activeIndex === 0,
              'text-black': activeIndex > 0,
            })}
          />
        </button>

        <div className="flex items-center gap-2">
          {elementsArray.map((_, index) => (
            <div
              key={`nav-dot-${index}`}
              className={cn('relative flex h-2 items-center text-clip rounded-[1px] bg-moss-grey-150', {
                'w-2': index !== activeIndex,
                'w-8': index === activeIndex,
              })}
            >
              {index === activeIndex && (
                <motion.div
                  ref={progressBarRef}
                  className="absolute z-10 h-2 w-full origin-left rounded-[1px] bg-neon-450"
                />
              )}
            </div>
          ))}
        </div>

        <button
          onClick={() => swiperInstance?.slideNext()}
          className="flex items-center justify-center"
          aria-label="Next slide"
        >
          <Icon
            name="arrow"
            className={cn('h-3 w-[13.595px]', {
              'text-black': activeIndex < posts.length - 1,
              'text-moss-grey-400': activeIndex === posts.length - 1,
            })}
          />
        </button>
      </div>
    </div>
  )
}

const FeaturedPost = ({
  post,
  index,
  activeIndex,
  slideRefs,
  maxMobileHeight,
  buttonLabel,
  pageUid,
  componentIndex,
  domainCountry,
}: {
  post: ResolvedPost
  index: number
  activeIndex: number
  slideRefs: React.MutableRefObject<(HTMLDivElement | null)[]>
  maxMobileHeight: number
  buttonLabel?: string
  pageUid: string
  componentIndex: number
  domainCountry: DomainCountry
}) => {
  const {linkResolver} = useLinkResolver()
  const postDoc = post.postDocument
  const postData = postDoc.data

  const date = postData.date ? formatDate(postData.date) : null
  const category = useGetBlogPostCategory(postData.category)
  const title = postData.title ?? ''
  const description = postData.description ?? ''
  const image = postData.meta_image

  const href = postDoc.uid ? linkResolver(postDoc) : null

  return (
    <div className="group/card relative w-full text-clip rounded-[8px] border border-solid border-black/10 bg-moss-white">
      {href && (
        <Link
          href={href}
          className="absolute inset-0 z-10 rounded-[8px] focus-visible:ring-2 focus-visible:ring-green-neon focus-visible:ring-offset-2"
          aria-label={title}
          onClick={() => {
            track({
              event: EVENTS.CLICK,
              mktgId: `carousel-featured-posts-${index}`,
              pageName: pageUid,
              location: `component-${componentIndex}-integrations-section`,
              dataType: 'primary-button',
            })

            trackMossAnalytics({
              event: 'interaction',
              interaction: {
                element: 'featured-post-card',
                elementText: title,
                location: `component-${componentIndex}-integrations-section`,
                type: 'click',
              },
              page: {type: pageUid || 'unknown', domainCountry},
            })
          }}
        />
      )}
      <div
        ref={(el): void => {
          slideRefs.current[index] = el
        }}
        className={cn(
          'box-border flex w-full flex-col items-start gap-6 text-clip rounded-[inherit] p-6 transition-opacity duration-300',
          'max-lg:justify-between',
          'lg:flex-row lg:justify-center lg:p-8',
          {
            'lg:opacity-30': index !== activeIndex,
          },
        )}
        style={maxMobileHeight > 0 ? {minHeight: `${maxMobileHeight}px`} : undefined}
      >
        {/* Content - Mobile/Tablet */}
        <div className="flex w-full flex-col gap-6 lg:hidden">
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-4">
              <div className="flex h-[22px] items-center gap-1 rounded-4 bg-ui-blue-450 px-2.5 py-2">
                <p className="text-detail text-moss-white">{category}</p>
              </div>
              {date && (
                <div className="flex flex-col justify-center">
                  <p className="text-detail text-moss-grey-400">{date}</p>
                </div>
              )}
            </div>
            {title && <h2 className="text-h4 leading-normal text-black">{title}</h2>}
            {description && <p className="text-tg-body-m max-w-[380px] text-moss-grey-550">{description}</p>}
          </div>

          {isFilled.image(image) && (
            <PrismicNextImage field={image} className="aspect-[556/320] w-full rounded-[6px] object-cover" />
          )}
        </div>

        {/* Button - Mobile/Tablet */}
        {href && (
          <Link
            href={href}
            className="relative flex h-[38px] w-fit items-center gap-[14px] self-end rounded-[4px] bg-moss-white pl-[14px] pr-[7px] transition-colors duration-100 group-hover/card:bg-moss-grey-100 lg:hidden"
            onClick={() => {
              track({
                event: EVENTS.CLICK,
                mktgId: `carousel-featured-posts-${index}`,
                pageName: pageUid,
                location: `component-${componentIndex}-integrations-section`,
                dataType: 'primary-button',
              })

              trackMossAnalytics({
                event: 'interaction',
                interaction: {
                  element: 'primary-button',
                  elementText: buttonLabel || 'Read article',
                  location: `component-${componentIndex}-integrations-section`,
                  type: 'click',
                },
                page: {type: pageUid || 'unknown', domainCountry},
              })
            }}
          >
            <span className="text-button-s uppercase text-black">{buttonLabel || 'Read article'}</span>
            <div className="flex size-6 items-center justify-center rounded-[2px] bg-green-neon">
              <Icon name="arrow" className="h-2 w-2.5 text-green-dark" />
            </div>
            <div className="pointer-events-none absolute inset-0 rounded-[4px] border border-moss-black/[10%]" />
          </Link>
        )}

        {/* Content - Desktop */}
        <div className="hidden min-w-0 flex-col gap-6 text-clip lg:flex lg:min-h-[320px] lg:flex-1">
          <div className="flex flex-1 flex-col gap-4">
            <div className="flex items-center gap-4">
              <div className="flex h-[22px] items-center gap-1 rounded-4 bg-ui-blue-450 px-2.5 py-2">
                <p className="text-detail text-moss-white">{category}</p>
              </div>
              {date && (
                <div className="flex flex-col justify-center">
                  <p className="text-detail text-moss-grey-400">{date}</p>
                </div>
              )}
            </div>
            <h2 className="text-h4 leading-normal text-black">{title}</h2>
            <p className="max-w-[302px] text-16 leading-5 text-moss-grey-550">{description}</p>
          </div>

          {/* Button - Desktop */}
          {href && (
            <Link
              href={href}
              className="relative flex h-[38px] w-fit items-center gap-[14px] self-start rounded-[4px] bg-moss-white pl-[14px] pr-[7px] transition-colors duration-100 group-hover/card:bg-moss-grey-100"
              onClick={() => {
                track({
                  event: EVENTS.CLICK,
                  mktgId: `carousel-featured-posts-${index}`,
                  pageName: pageUid,
                  location: `component-${componentIndex}-integrations-section`,
                  dataType: 'primary-button',
                })

                trackMossAnalytics({
                  event: 'interaction',
                  interaction: {
                    element: 'primary-button',
                    elementText: buttonLabel || 'Read article',
                    location: `component-${componentIndex}-integrations-section`,
                    type: 'click',
                  },
                  page: {type: pageUid || 'unknown', domainCountry},
                })
              }}
            >
              <span className="text-button-s uppercase text-black">{buttonLabel || 'Read article'}</span>
              <div className="flex size-6 items-center justify-center rounded-[2px] bg-green-neon">
                <Icon name="arrow" className="h-2 w-2.5 text-green-dark" />
              </div>
              <div className="pointer-events-none absolute inset-0 rounded-[4px] border border-moss-black/[10%]" />
            </Link>
          )}
        </div>

        {/* Image - Desktop */}
        <div className="hidden h-full flex-row items-center lg:flex lg:min-w-[50%] lg:justify-end lg:self-center">
          {isFilled.image(image) && (
            <PrismicNextImage
              field={image}
              className="aspect-[556/320] size-full max-w-[556px] rounded-[6px] object-cover"
            />
          )}
        </div>
      </div>
    </div>
  )
}
